/**
 * Wizard Step 3 — Review & submit (COL-252).
 *
 * Read-only summary with "Edit" jump-back links. Owner + revenue are shown
 * per apartment (no global lessor/revenue section anymore). Submit button is
 * owned by the wizard page wrapper.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Check,
  CircleAlert,
  Clock,
  Loader2,
  Mail,
  Pencil,
  Send,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useWizard } from "./wizardContext";
import {
  bedroomsOf,
  CHECKLIST_ITEM_LABELS,
  checklistItemStatuses,
  computeChecklistProgress,
} from "./checklist";
import { buildOwnerEmailDraft } from "./owner-missing-items-email";
import { formatEUR } from "@/lib/v2-format";
import type { RevenueModel } from "@/types/property";
import { useAuth } from "@/contexts/AuthContext";
import { useLessor } from "@/hooks/v2/useLessors";
import { collectionModeLabels } from "@/lib/collection-mode";
import { useOperatorBranding } from "@/hooks/useOperatorBranding";
import { useLanguage } from "@/contexts/LanguageContext";

type Props = {
  onJump: (step: number) => void;
};

function revenueModelLabels(
  t: (key: string, fallback?: string) => string
): Record<RevenueModel, string> {
  return {
    full_rent: t("admin.wizard.step3.revenueFullRent", "Full rent"),
    pct_of_rent: t("admin.wizard.step3.revenuePctOfRent", "% of rent"),
    flat_fee_per_room: t(
      "admin.wizard.step3.revenueFlatFeePerRoom",
      "Flat fee/room"
    ),
  };
}

export function Step3Review({ onJump }: Props) {
  const { state } = useWizard();
  const { isOwnerPortalOnly } = useAuth();
  const { platformName } = useOperatorBranding();
  const { t } = useLanguage();
  const COLLECTION_MODE_LABELS = collectionModeLabels(null, platformName, t);
  const REVENUE_MODEL_LABELS = revenueModelLabels(t);

  return (
    <div className="space-y-4">
      <Section
        title={t("admin.wizard.step3.property", "Property")}
        stepNumber={1}
        onJump={onJump}
      >
        <Row
          label={t("admin.wizard.step3.titleField", "Title")}
          value={state.property.title || <Missing />}
        />
        <Row
          label={t("admin.wizard.step3.address", "Address")}
          value={state.property.address || <Missing />}
        />
        <Row
          label={t("admin.wizard.step3.streetName", "Street name")}
          value={state.property.street_name || <Missing />}
        />
        <Row
          label={t("admin.wizard.step3.streetNumber", "Street number")}
          value={state.property.street_number || <Missing />}
        />
        <Row
          label={t("admin.wizard.step3.city", "City")}
          value={state.property.city || <Missing />}
        />
        <Row
          label={t("admin.wizard.step3.postalCode", "Postal code (PLZ)")}
          value={state.property.zip_code || <Missing optional />}
        />
        <Row
          label={t("admin.wizard.step3.type", "Type")}
          value={state.property.type || <Missing optional />}
        />
        {!isOwnerPortalOnly && (
          <Row
            label={t("admin.wizard.step3.revenueModel", "Revenue model")}
            value={`${COLLECTION_MODE_LABELS[state.property.collectionMode]} · ${REVENUE_MODEL_LABELS[state.property.revenueModel]}`}
          />
        )}
        {state.property.description && (
          <Row
            label={t("admin.wizard.step3.description", "Description")}
            value={state.property.description}
          />
        )}
        <Row
          label={t("admin.wizard.step3.photos", "Photos")}
          value={
            state.property.images.length > 0 ? (
              <span className="flex items-center gap-2 justify-end">
                <span>{state.property.images.length}</span>
                <PreviewStrip urls={state.property.images} max={4} />
              </span>
            ) : (
              <Missing optional label={t("admin.wizard.step3.none", "None")} />
            )
          }
        />
      </Section>

      <Section
        title={t(
          "admin.wizard.step3.apartmentsCount",
          "Apartments ({n})"
        ).replace("{n}", String(state.apartments.length))}
        stepNumber={2}
        onJump={onJump}
      >
        {state.apartments.length === 0 ? (
          <Missing
            label={t(
              "admin.wizard.step3.noApartmentsAdded",
              "No apartments added"
            )}
          />
        ) : (
          <div className="space-y-3">
            {state.apartments.map((apt, i) => {
              const hasOwner = !!(apt.ownerUserId || apt.lessorId);
              // apt.revenueModel === null means "inherit from property"
              // (COL-225) — resolve the effective model + fee the same way
              // resolveEffectiveRevenue does for persisted rows: an
              // apartment override carries its own fee fields; an inheriting
              // apartment uses the property's.
              const isOverride = apt.revenueModel != null;
              const effectiveRevenueModel =
                apt.revenueModel ?? state.property.revenueModel;
              const effectiveFeePct = isOverride
                ? apt.coliviqFeePct
                : state.property.coliviqFeePct;
              const effectiveFeeAmount = isOverride
                ? apt.coliviqFeeAmount
                : state.property.coliviqFeeAmount;
              const effectiveMasterRent = isOverride
                ? apt.masterRent
                : state.property.masterRent;
              const fee =
                effectiveRevenueModel === "pct_of_rent"
                  ? effectiveFeePct
                    ? `${effectiveFeePct}%`
                    : "—"
                  : effectiveRevenueModel === "flat_fee_per_room"
                    ? `${formatEUR(Number(effectiveFeeAmount)) ?? "—"}/${t("admin.wizard.review.perRoomSuffix", "room")}${
                        Number(apt.numRooms) > 0
                          ? ` × ${apt.numRooms} = ${formatEUR(Number(effectiveFeeAmount) * Number(apt.numRooms)) ?? "—"}/${t("admin.wizard.review.perMonthSuffix", "mo")}`
                          : ""
                      }`
                    : effectiveMasterRent
                      ? `${formatEUR(Number(effectiveMasterRent)) ?? "—"}/${t("admin.wizard.review.perMonthMasterSuffix", "mo master")}`
                      : "—";
              return (
                <div key={apt.localId} className="rounded-md border">
                  <div className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/30 border-b">
                    <span className="text-sm font-semibold">
                      {apt.label || `Apartment ${i + 1}`}
                      {apt.floor ? (
                        <span className="font-normal text-muted-foreground">
                          {" "}
                          · {apt.floor}
                        </span>
                      ) : null}
                      {apt.size_sqm ? (
                        <span className="font-normal text-muted-foreground">
                          {" "}
                          · {apt.size_sqm} m²
                        </span>
                      ) : null}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {apt.rooms.length}{" "}
                      {apt.rooms.length === 1
                        ? t("admin.wizard.step3.roomSingular", "room")
                        : t("admin.wizard.step3.roomsPlural", "rooms")}
                    </span>
                  </div>
                  {!isOwnerPortalOnly && (
                    <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b text-xs">
                      {hasOwner ? (
                        <Badge variant="secondary" className="font-normal">
                          {t("admin.wizard.step3.ownerSet", "Owner set")}
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="font-normal text-amber-600 border-amber-300"
                        >
                          {t("admin.wizard.step3.noOwner", "No owner")}
                        </Badge>
                      )}
                      <Badge variant="outline" className="font-normal">
                        {REVENUE_MODEL_LABELS[effectiveRevenueModel]}
                        {!isOverride &&
                          ` ${t("admin.wizard.step3.inheritedSuffix", "(inherited)")}`}
                      </Badge>
                      {/* Only worth showing when it deviates — a blank field
                          just means "the owner's default", already implied. */}
                      {apt.repName.trim() && (
                        <Badge variant="outline" className="font-normal">
                          {t(
                            "admin.wizard.step3.representorLabel",
                            "Representor:"
                          )}{" "}
                          {apt.repName.trim()}
                        </Badge>
                      )}
                      <span className="text-muted-foreground">{fee}</span>
                    </div>
                  )}
                  {apt.rooms.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-muted-foreground">
                      {t(
                        "admin.wizard.step3.noRoomsInApartment",
                        "No rooms in this apartment."
                      )}
                    </p>
                  ) : (
                    <table className="w-full text-sm">
                      <tbody>
                        {apt.rooms.map((r) => (
                          <tr key={r.localId} className="border-t">
                            <td className="px-3 py-1.5 font-medium">
                              {r.title || "—"}
                            </td>
                            <td className="px-3 py-1.5 text-right tabular-nums">
                              {r.price
                                ? (formatEUR(Number(r.price)) ?? "—")
                                : "—"}
                            </td>
                            <td className="px-3 py-1.5 hidden sm:table-cell text-muted-foreground">
                              {r.size_sqm ? `${r.size_sqm} m²` : "—"}
                            </td>
                            <td className="px-3 py-1.5 hidden sm:table-cell text-muted-foreground">
                              {r.floor || "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Section>

      <Section
        title={t("admin.wizard.step3.checklist", "Checklist")}
        stepNumber={3}
        onJump={onJump}
      >
        {state.apartments.length === 0 ? (
          <Missing
            label={t(
              "admin.wizard.step3.noApartmentsAdded",
              "No apartments added"
            )}
          />
        ) : (
          <div className="space-y-3">
            {state.apartments.map((apt, i) => {
              const opts = {
                bedrooms: bedroomsOf(apt.numRooms),
                keynestAvailable: state.property.keynestAvailable,
              };
              const progress = computeChecklistProgress(apt.checklist, opts);
              const statuses = checklistItemStatuses(apt.checklist, opts);
              return (
                <div key={apt.localId} className="rounded-md border">
                  <div className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/30 border-b">
                    <span className="text-sm font-semibold">
                      {apt.label || `Apartment ${i + 1}`}
                    </span>
                    <Badge
                      variant="outline"
                      className={
                        progress.blocked
                          ? "border-destructive/40 bg-destructive/10 text-destructive tabular-nums"
                          : "tabular-nums text-muted-foreground"
                      }
                    >
                      {progress.done}/{progress.total}
                    </Badge>
                  </div>
                  <div className="grid sm:grid-cols-2">
                    {CHECKLIST_ITEM_LABELS.map(({ key, i18nKey, fallback }) => {
                      const status = statuses[key];
                      return (
                        <div
                          key={key}
                          className="flex items-center gap-2 border-t px-3 py-1.5 text-xs"
                        >
                          {status === "done" ? (
                            <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                          ) : status === "requested" ? (
                            <Clock className="h-3.5 w-3.5 shrink-0 text-amber-600" />
                          ) : (
                            <CircleAlert className="h-3.5 w-3.5 shrink-0 text-destructive" />
                          )}
                          <span className="flex-1 truncate">
                            {t(i18nKey, fallback)}
                          </span>
                          <span
                            className={
                              status === "done"
                                ? "text-muted-foreground"
                                : status === "requested"
                                  ? "text-amber-600"
                                  : "text-destructive"
                            }
                          >
                            {status === "done"
                              ? t("admin.wizard.step3.statusDone", "Done")
                              : status === "requested"
                                ? t(
                                    "admin.wizard.step3.statusRequested",
                                    "Requested"
                                  )
                                : t(
                                    "admin.wizard.step3.statusMissing",
                                    "Missing"
                                  )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {progress.blocked && (
                    <div className="border-t px-3 py-2 text-xs">
                      <Badge
                        variant="outline"
                        className="font-normal text-destructive border-destructive/40"
                      >
                        {t(
                          "admin.wizard.step3.checklistBlocked",
                          "No smoke detector — go-live blocked"
                        )}
                      </Badge>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {!isOwnerPortalOnly && <OwnerEmailDraft />}
    </div>
  );
}

/**
 * Staff-only (COL-529, point 4): an editable German draft listing the items
 * the admin flagged "request from owner" in the Checkliste step, grouped by
 * section. The admin edits To/Subject/Body and sends it directly from here
 * (from info@coliviq.de, via send-admin-email) — per Neel's feedback, there is
 * no send-on-submit.
 *
 * Fields are bound to wizard state (`state.ownerEmail`) so they survive a
 * remount when the admin jumps back to an earlier step and returns. The draft
 * is prefilled from the checklist exactly once (guarded by `ownerEmail.prefilled`)
 * so a later re-render never clobbers the admin's edits; `sent` likewise persists
 * so the same draft can't be sent twice. Own component so its hooks never run on
 * the owner path.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function OwnerEmailDraft() {
  const { state, setOwnerEmail } = useWizard();
  const { t } = useLanguage();
  const { toast } = useToast();
  const [sending, setSending] = useState(false);
  const { ownerEmail } = state;

  const draft = useMemo(
    () =>
      buildOwnerEmailDraft({
        propertyTitle: state.property.title,
        address: state.property.address,
        apartments: state.apartments.map((a) => ({ checklist: a.checklist })),
      }),
    [state.property.title, state.property.address, state.apartments]
  );

  // Best-guess recipient: the first apartment that has an owner entity.
  const lessorId = state.apartments.find((a) => a.lessorId)?.lessorId ?? null;
  const { data: lessor } = useLessor(lessorId);

  // Prefill subject/body + the enable flag exactly once. After that the admin
  // owns the fields; re-renders (lessor resolving, jumping between steps) leave
  // them alone. `enabled` defaults on only when something is actually requested.
  useEffect(() => {
    if (ownerEmail.prefilled) return;
    setOwnerEmail({
      subject: draft.subject,
      body: draft.body,
      prefilled: true,
    });
  }, [ownerEmail.prefilled, draft, setOwnerEmail]);

  // Fill the recipient once it resolves, but never clobber a manual edit.
  useEffect(() => {
    if (lessor?.email && !ownerEmail.to) setOwnerEmail({ to: lessor.email });
  }, [lessor?.email, ownerEmail.to, setOwnerEmail]);

  const canSend =
    !sending &&
    !ownerEmail.sent &&
    draft.count > 0 &&
    EMAIL_RE.test(ownerEmail.to.trim());

  // Sends the request directly from the review step (COL-529 point 4). The
  // property need not exist yet — the email only lists the missing items — so
  // this is independent of the Create/Submit action.
  const send = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "send-admin-email",
        {
          body: {
            to: ownerEmail.to.trim(),
            subject: ownerEmail.subject,
            text: ownerEmail.body,
            sender: "info",
          },
        }
      );
      if (error) throw error;
      if (data?.success === false) throw new Error(data.error || "send failed");
      setOwnerEmail({ sent: true });
      toast({
        title: t("admin.wizard.step3.email.sentToast", "Email sent to owner"),
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: t("admin.wizard.step3.email.sendFailed", "Email not sent"),
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="p-5 space-y-3">
      <header className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-base font-semibold">
          {t(
            "admin.wizard.step3.email.heading",
            "Request documents from owner"
          )}
        </h3>
        {draft.count === 0 && (
          <Badge variant="secondary" className="font-normal">
            {t(
              "admin.wizard.step3.email.nothingRequested",
              "Nothing requested"
            )}
          </Badge>
        )}
      </header>
      <div className="space-y-1">
        <Label htmlFor="owner-email-to" className="text-xs">
          {t("admin.wizard.step3.email.to", "To")}
        </Label>
        <Input
          id="owner-email-to"
          type="email"
          value={ownerEmail.to}
          onChange={(e) => setOwnerEmail({ to: e.target.value, sent: false })}
          placeholder="owner@example.com"
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="owner-email-subject" className="text-xs">
          {t("admin.wizard.step3.email.subject", "Subject")}
        </Label>
        <Input
          id="owner-email-subject"
          value={ownerEmail.subject}
          onChange={(e) =>
            setOwnerEmail({ subject: e.target.value, sent: false })
          }
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="owner-email-body" className="text-xs">
          {t("admin.wizard.step3.email.body", "Message")}
        </Label>
        <Textarea
          id="owner-email-body"
          value={ownerEmail.body}
          onChange={(e) => setOwnerEmail({ body: e.target.value, sent: false })}
          rows={12}
          className="font-mono text-xs"
        />
      </div>
      <div className="flex items-center justify-end gap-3">
        {ownerEmail.sent && (
          <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Check className="h-4 w-4 text-primary" />
            {t("admin.wizard.step3.email.sentLabel", "Sent to owner")}
          </span>
        )}
        <Button type="button" onClick={send} disabled={!canSend}>
          {sending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Send className="mr-2 h-4 w-4" />
          )}
          {t("admin.wizard.step3.email.send", "Send email")}
        </Button>
      </div>
    </Card>
  );
}

function Section({
  title,
  stepNumber,
  onJump,
  children,
}: {
  title: string;
  stepNumber: number;
  onJump: (step: number) => void;
  children: React.ReactNode;
}) {
  const { t } = useLanguage();
  return (
    <Card className="p-5 space-y-3">
      <header className="flex items-center justify-between">
        <h3 className="text-base font-semibold">{title}</h3>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onJump(stepNumber)}
        >
          <Pencil className="mr-2 h-3.5 w-3.5" />
          {t("admin.wizard.step3.edit", "Edit")}
        </Button>
      </header>
      <div className="space-y-2 text-sm">{children}</div>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs text-muted-foreground shrink-0">{label}</dt>
      <dd className="font-medium text-right">{value}</dd>
    </div>
  );
}

function Missing({ optional, label }: { optional?: boolean; label?: string }) {
  const { t } = useLanguage();
  return (
    <span className={optional ? "text-muted-foreground" : "text-destructive"}>
      {label ??
        (optional
          ? t("admin.wizard.step3.notSet", "Not set")
          : t("admin.wizard.step3.missing", "Missing"))}
    </span>
  );
}

function PreviewStrip({
  urls,
  max,
  small,
}: {
  urls: string[];
  max: number;
  small?: boolean;
}) {
  const visible = urls.slice(0, max);
  const remaining = urls.length - visible.length;
  const size = small ? "h-7 w-7" : "h-9 w-9";
  return (
    <span className="inline-flex items-center -space-x-1">
      {visible.map((url, i) => (
        <span
          key={`${url}-${i}`}
          className={`${size} rounded border bg-muted overflow-hidden ring-2 ring-background inline-block`}
        >
          <img
            src={url}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </span>
      ))}
      {remaining > 0 && (
        <span
          className={`${size} rounded border bg-muted ring-2 ring-background inline-flex items-center justify-center text-[10px] font-medium text-muted-foreground`}
        >
          +{remaining}
        </span>
      )}
    </span>
  );
}
